import moment from 'moment';

/**
 * ROAR-5001 (P1.14) — the top-nav credits badge: wire values, copy inputs, and the one pure
 * function that turns a `GET /billing/credits/status` payload into what the badge draws.
 *
 * Kept out of the component so every state in the Story's badge table is testable without
 * rendering a header.
 */

/**
 * The wire values of `banner_state`. Mirrors api-backend-provapt's `creditBannerStates`
 * (`src/utils/constants.js`), whose `resolveBannerState` owns the precedence between them —
 * `past_due` > `trial_exhausted` > `exhausted` > `warning`. The FE never re-derives a state from
 * `pct` or `available`: it would inherit thresholds that can drift from the backend's.
 */
export const CREDIT_BANNER_STATES = Object.freeze({
  NORMAL: 'normal',
  WARNING: 'warning',
  EXHAUSTED: 'exhausted',
  TRIAL_EXHAUSTED: 'trial_exhausted',
  PAST_DUE: 'past_due',
});

/**
 * What the badge renders. `TRIAL` is not a wire value: it is `normal` while the subscription is
 * trialing (`trial_days_left` is non-null only then), which the Story draws differently.
 */
export const CREDIT_BADGE_KINDS = Object.freeze({
  ...CREDIT_BANNER_STATES,
  TRIAL: 'trial',
});

export const CREDIT_BADGE_TONES = Object.freeze({
  NEUTRAL: 'neutral',
  WARNING: 'warning',
  DANGER: 'danger',
});

/** The badge's call to action. Each is shown as a link only to users allowed to act on billing. */
export const CREDIT_BADGE_ACTIONS = Object.freeze({
  VIEW_USAGE: 'view_usage',
  START_EARLY: 'start_early',
  UPDATE_PAYMENT: 'update_payment',
});

/** Settings-registry keys the links open (`utils/constants/settingsRegistry.js`). */
export const CREDIT_BADGE_ACTION_SETTINGS_TAB = Object.freeze({
  [CREDIT_BADGE_ACTIONS.VIEW_USAGE]: 'credits_usage',
  [CREDIT_BADGE_ACTIONS.UPDATE_PAYMENT]: 'payments_invoices',
});

/**
 * `billing.features` catalog keys the hover card's "You can cover" lines quote. Both cards label the
 * GenPT line "GenPT DAST"; the trial card quotes the web-app test (`genpt_web`), the paid card the
 * API test (`genpt_api`). Both quote EASM.
 */
export const CREDIT_COVERAGE_FEATURES = Object.freeze({
  GENPT_DAST: 'genpt_web',
  GENPT_API: 'genpt_api',
  EASM: 'easm',
});

/**
 * The trial plan's EASM subdomain cap, as stated in the trial hover card. It lives in the backend's
 * `plans.features.easm.max_subdomains`, which no portal-facing endpoint exposes yet — if it ever
 * does, read it from there and delete this.
 */
export const TRIAL_EASM_MAX_SUBDOMAINS = 20;

/**
 * "September 24, 2026". Formatted in UTC: `resets_at` is a UTC midnight, and rendering it in a
 * western timezone would print the day before.
 *
 * @param {string|null|undefined} value ISO timestamp.
 * @returns {string|null}
 */
export const formatCreditDate = (value) => {
  if (!value) return null;
  const date = moment.utc(value);
  return date.isValid() ? date.format('MMMM D, YYYY') : null;
};

/**
 * "1 credit" / "500 credits" / "1,250 credits".
 *
 * @param {number} count
 * @returns {string}
 */
export const formatCredits = (count) =>
  `${Number(count).toLocaleString('en-US')} ${count === 1 ? 'credit' : 'credits'}`;

/** "1 day" / "7 days". */
export const formatDays = (count) => `${count} ${count === 1 ? 'day' : 'days'}`;

/**
 * How many runs of a feature `available` covers at its default settings, or null when the feature
 * has no price (`default_estimated_credits` null or 0 — `included`, or an unpriceable rule).
 *
 * @param {number|null} available
 * @param {number|null|undefined} defaultEstimate
 * @returns {number|null}
 */
export const coverageCount = (available, defaultEstimate) => {
  if (available == null || !defaultEstimate || defaultEstimate <= 0) return null;
  return Math.max(0, Math.floor(available / defaultEstimate));
};

/**
 * The renewal date for a trialing company ("automatically renews on <trial_end>").
 *
 * The trial lot's `resets_at` is the trial end. When there is no cycle lot to read it from, fall
 * back to `days_until_renewal`, which counts to the subscription's `current_period_end` — the same
 * instant while trialing.
 *
 * @param {object} status The credits-status payload.
 * @returns {string|null} ISO timestamp.
 */
const trialRenewalDate = (status) => {
  if (status?.cycle?.resets_at) return status.cycle.resets_at;
  if (status?.days_until_renewal == null) return null;
  return moment.utc().add(status.days_until_renewal, 'days').toISOString();
};

/**
 * Resolves the credits-status payload into the badge's view model, or null when there is nothing
 * to show.
 *
 * Null when the payload is missing, or when `available` is null outside `past_due` — the backend
 * sends null when the company has no balance row, and stating "0 credits" for that would read as
 * "out of credits". `past_due` renders regardless: it does not mention a balance, and it is the one
 * state AC5 must never drop.
 *
 * @param {object|null|undefined} status The `GET /billing/credits/status` payload.
 * @returns {null|{kind: string, tone: string, action: string|null, available: number|null,
 *   trialDaysLeft: number|null, daysUntilRenewal: number|null, pct: number|null, used: number|null, allotment: number|null,
 *   resetsOn: string|null, renewsOn: string|null}}
 */
export const resolveCreditBadge = (status) => {
  if (!status) return null;
  const state = status.banner_state;
  const available = status.available ?? null;
  if (state !== CREDIT_BANNER_STATES.PAST_DUE && available == null) return null;

  const cycle = status.cycle || null;
  const base = {
    available,
    trialDaysLeft: status.trial_days_left ?? null,
    // Shown on the paid (`normal`) pill: monthly credits expire at the end of the period, so a
    // paying customer needs the countdown too. Keyed on the subscription's period end, which the
    // backend counts for everyone (`get.creditsStatus.js`).
    daysUntilRenewal: status.days_until_renewal ?? null,
    pct: cycle?.pct ?? null,
    used: cycle?.used ?? null,
    allotment: cycle?.allotment ?? null,
    resetsOn: formatCreditDate(cycle?.resets_at),
    renewsOn: null,
  };

  switch (state) {
    case CREDIT_BANNER_STATES.PAST_DUE:
      return {
        ...base,
        kind: CREDIT_BADGE_KINDS.PAST_DUE,
        tone: CREDIT_BADGE_TONES.DANGER,
        action: CREDIT_BADGE_ACTIONS.UPDATE_PAYMENT,
        // The stale lot's reset date is wrong by definition during past_due (Story tech notes).
        resetsOn: null,
      };
    case CREDIT_BANNER_STATES.TRIAL_EXHAUSTED:
      return {
        ...base,
        kind: CREDIT_BADGE_KINDS.TRIAL_EXHAUSTED,
        tone: CREDIT_BADGE_TONES.DANGER,
        action: CREDIT_BADGE_ACTIONS.START_EARLY,
        renewsOn: formatCreditDate(trialRenewalDate(status)),
      };
    case CREDIT_BANNER_STATES.EXHAUSTED:
      return {
        ...base,
        kind: CREDIT_BADGE_KINDS.EXHAUSTED,
        tone: CREDIT_BADGE_TONES.DANGER,
        action: CREDIT_BADGE_ACTIONS.VIEW_USAGE,
      };
    case CREDIT_BANNER_STATES.WARNING:
      return {
        ...base,
        kind: CREDIT_BADGE_KINDS.WARNING,
        tone: CREDIT_BADGE_TONES.WARNING,
        action: CREDIT_BADGE_ACTIONS.VIEW_USAGE,
      };
    default:
      // `normal`, and any value a newer backend adds before the portal learns it: a plain balance
      // is the safe rendering for an unknown state.
      return {
        ...base,
        kind: base.trialDaysLeft != null ? CREDIT_BADGE_KINDS.TRIAL : CREDIT_BADGE_KINDS.NORMAL,
        tone: CREDIT_BADGE_TONES.NEUTRAL,
        action: null,
      };
  }
};
